import { ICrewData, IReceiptData, IYachtData } from "../ApiCalls";
import { LocalStorage } from "../LocalStorage";
import { globalStore } from "./store";
import { dialogsSlice, TDialogTypeState } from "./slices/dialogsSlice";
import { selectedUserSlice } from "./slices/userSlice";
import { statusesSlice } from "./slices/statusesSlice";
import { selectedYachtSlice } from "./slices/yachtSlice";
import { tsSlice } from "./slices/tsSlice";
import { receiptChangesSlice } from "./slices/receiptChangesSlice";
import { IReceiptUploadFormData, receiptUploadSlice } from "./slices/receiptUploadSlice";
import { GlobalVariables } from "../GlobalVariables";
import { GlobalConstants } from "../GlobalConstants";

export interface IApiCurrencyData {
  rate: number;
  timestamp: number;
}

export class ReduxUpdater {
    private static _currentDialog: TDialogTypeState = null;
    private static _currentYacht: IYachtData | null = null;
    private static _currentYachtCurrencies: string[] = [];
    private static _authUser: ICrewData | null = null;
    // private static _selectedUser: ICrewData | null = null;
    // private static _currentNav = -1;
    // private static _receiptChanges: IReceiptData = {} as IReceiptData;
    private static _yachtCrew: ICrewData[] | null = null;
    private static _yachtCrewCards: ICrewData[] | null = null;
    private static _offCanvas: IReceiptData | null = null;

    static get authenticated(): boolean {
        if (this._authUser === null) {
            this._authUser = LocalStorage.authUser;
        }
        return this._authUser !== null;
    }

    static get adminAuthenticatedUser(): ICrewData | null {
        if (this._authUser === null) {
            this._authUser = LocalStorage.authUser;
        }
        return this._authUser;
    }

    static get authenticatedUser(): ICrewData | null {
        if (this._authUser === null) {
            this._authUser = LocalStorage.authUser;
        }
        return this._authUser?.impersonate ?? this._authUser;
    }

    static set authenticatedUser(newUser: ICrewData | null) {
        console.log('ReduxUpdater.authenticatedUser:', newUser);
        this._authUser = newUser;
        LocalStorage.authUser = newUser;
        globalStore.dispatch(tsSlice.actions.reloadAuthenticatedUser());
    }

    static get currentDialog(): TDialogTypeState {
        return this._currentDialog;
    }

    static set currentDialog(newDialog: TDialogTypeState) {
        console.log('ReduxUpdater.currentDialog:', newDialog);
        this._currentDialog = newDialog;
        globalStore.dispatch(dialogsSlice.actions.showDialog(newDialog));
    }

    static get savedCurrentYacht(): IYachtData | null {
        if (this._currentYacht === null) {
            this._currentYacht = LocalStorage.yacht;
        }
        return this._currentYacht;
    }

    static set currentYacht(newYacht: IYachtData | null) {
        console.log('ReduxUpdater.currentYacht:', newYacht);
        this._currentYacht = newYacht;
        LocalStorage.yacht = newYacht;
        globalStore.dispatch(selectedYachtSlice.actions.selectYacht(newYacht ? {...newYacht} : null));
    }

    static get currentYachtCurrencies(): string[] {
        return this._currentYachtCurrencies;
    }

    static set currentYachtCurrencies(newYachtCurrencies: string[]) {
        console.log('ReduxUpdater.currentYachtCurrencies:', newYachtCurrencies);
        this._currentYachtCurrencies = newYachtCurrencies;
        LocalStorage.yachtCurrencies = newYachtCurrencies;
        globalStore.dispatch(selectedYachtSlice.actions.selectYachtCurrencies(newYachtCurrencies));
    }

    static get offCanvas(): IReceiptData | null {
        return this._offCanvas;
    }

    static set offCanvas(newOffCanvas: IReceiptData | null) {
        console.log('ReduxUpdater.offCanvas:', newOffCanvas);
        this._offCanvas = newOffCanvas;
        globalStore.dispatch(tsSlice.actions.reloadOffCanvas());
    }

    // static get selectedUser(): ICrewData | null {
    //     if (this._currentUser === null) {
    //         this._currentUser = LocalStorage.user;
    //     }
    //     return this._currentUser;
    // }

    // static get receiptChanges(): IReceiptData {
    //     return this._receiptChanges;
    // }

    static set receiptChanges(newReceiptData: IReceiptData) {
        console.log('ReduxUpdater.receiptChanges:', newReceiptData);
        // this._receiptChanges = newReceiptData;
        globalStore.dispatch(receiptChangesSlice.actions.updateReceiptChanges(newReceiptData));
    }

    static set receiptUpload(newUploadReceiptData: IReceiptUploadFormData) {
        console.log('ReduxUpdater.receiptUpload:', newUploadReceiptData);
        globalStore.dispatch(receiptUploadSlice.actions.updateUploadReceiptData(newUploadReceiptData));
    }

    static set selectedUserCurrency(newCurrency: string) {
        console.log('ReduxUpdater.selectedUserCurrency:', newCurrency);
        // this._selectedUser = newUser;
        LocalStorage.userCurrency = newCurrency;
        globalStore.dispatch(selectedUserSlice.actions.selectUserCurrency(newCurrency));
    }

    /**
     * Crew member selected in expenses.  
     * This is only awailable to admin users to change in the UI.
     */
    static set selectedUser(newUser: ICrewData | null) {
        console.log('ReduxUpdater.selectedUser:', newUser);
        // this._selectedUser = newUser;
        LocalStorage.user = newUser;
        globalStore.dispatch(selectedUserSlice.actions.selectUser(newUser ? {...newUser} : null));
    }

    static get yachtCrew(): ICrewData[] {
        if (this._yachtCrew === null) {
            this._yachtCrew = LocalStorage.crew;
        }
        return this._yachtCrew;
    }

    static set yachtCrew(newCrew: ICrewData[]) {
        console.log('ReduxUpdater.yachtCrew:', newCrew);
        this._yachtCrew = newCrew;
        LocalStorage.crew = newCrew;
        globalStore.dispatch(tsSlice.actions.reloadYachtCrew());
    }

    static get yachtCrewCards(): ICrewData[] {
        if (this._yachtCrew === null) {
            this._yachtCrew = LocalStorage.crew;
        }
        return this._yachtCrew.filter(u => GlobalVariables.userHasCard(u)).sort(GlobalConstants.SORT.compareFnCrew);
    }

    static get yachtCrewCardsRest(): ICrewData[] {
        if (this._yachtCrewCards === null) {
            return [];
        }
        return this._yachtCrewCards?.filter(c => c.id !== (this._authUser?.id ?? -1));
    }

    /**
     * Curent tab selected in expenses.
     * 
     * 0: Missing Receipts  
     * 1: Petty$  
     * 2: Review & Confirm  
     * 3: Confirmed Receipts  
     */
    static set selectedNav(newNav: number) {
        console.log('ReduxUpdater.selectedNav:', newNav);
        LocalStorage.nav = newNav;
        globalStore.dispatch(statusesSlice.actions.setSelectedNav(newNav));
    }

    static set menuCollapsed(newState: boolean) {
        globalStore.dispatch(statusesSlice.actions.setMenuCollapsed(newState));
    }

    /**
     * Current position of receipt in the `Review & Confirm` tab.
     */
    static set selectedNavPos(newNavPos: number) {
        console.log('selectedNavPos:', newNavPos);
        LocalStorage.navPos = newNavPos;
        globalStore.dispatch(statusesSlice.actions.setSelectedNavPos(newNavPos));
    }

    static set documentLoading(state: boolean) {
        globalStore.dispatch(statusesSlice.actions.updateDocumentState(state));
    }

    static set documentLoadingError(state: boolean) {
        console.log('imageLoadingError:', state);
        globalStore.dispatch(statusesSlice.actions.updateDocumentErrorState(state));
    }

    static set documentPage(pages: number) {
        globalStore.dispatch(statusesSlice.actions.updateDocumentPage(pages));
    }

    static set documentPages(numPages: number) {
        globalStore.dispatch(statusesSlice.actions.updateDocumentPages(numPages));
    }

    static set confirmedReceiptsReloading(state: boolean) {
        globalStore.dispatch(statusesSlice.actions.updateConfirmedReceiptsState(state));
    }

    static set expensesReloading(state: boolean) {
        globalStore.dispatch(statusesSlice.actions.updateExpensesState(state));
    }

    static set formUpdating(state: boolean) {
        globalStore.dispatch(statusesSlice.actions.updateFormUpdatingState(state));
    }

    static set lastTransactionDateReloading(state: boolean) {
        globalStore.dispatch(statusesSlice.actions.updateLastTransactionReloadingState(state));
    }

    static set uploadedReceiptID(newState: number) {
        globalStore.dispatch(statusesSlice.actions.setUploadedReceiptID(newState));
    }

    static reloadConfirmedReceiptsData() {
        console.log('reloadConfirmedReceiptsData');
        this.confirmedReceiptsReloading = true;
        globalStore.dispatch(tsSlice.actions.reloadConfirmedReceipts());
    }

    static reloadCurrencies() {
        console.log('reloadCurrencies');
        globalStore.dispatch(tsSlice.actions.reloadCurrencies());
    }

    static reloadExpensesData() {
        console.log('reloadExpensesData');
        this.expensesReloading = true;
        // this.selectedNavPos = 0;
        globalStore.dispatch(tsSlice.actions.reloadExpenses());
    }

    static reloadImage() {
        console.log('reloadImage');
        globalStore.dispatch(tsSlice.actions.reloadImage());
    }

    static reloadLastTransactionDate() {
        console.log('reloadLastTransactionDate');
        this.lastTransactionDateReloading = true;
        globalStore.dispatch(tsSlice.actions.reloadTransactionDate());
    }

    static reloadPettyCashBalance() {
        console.log('reloadPettyCashBalance');
        globalStore.dispatch(tsSlice.actions.reloadPettyCash());
    }

    static reloadSelectedNav() {
        console.log('reloadSelectedNav');
        globalStore.dispatch(tsSlice.actions.reloadSelectedNavDate());
    }

    static autoInitPreline() {
        globalStore.dispatch(tsSlice.actions.reloadPreline());
    }
}
